import { Button, HStack, Input, Stack } from '@chakra-ui/react';
import { useMemo } from 'react';

export type Preset = 'thisMonth' | 'lastMonth' | 'last3Months' | 'thisYear' | 'custom';

export const PRESET_LABEL: Record<Preset, string> = {
  thisMonth: 'Este mes',
  lastMonth: 'Mes pasado',
  last3Months: 'Últimos 3 meses',
  thisYear: 'Este año',
  custom: 'Personalizado',
};

function isoDate(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function rangeFor(preset: Preset, today: Date = new Date()): { from: string; to: string } {
  const y = today.getUTCFullYear();
  const m = today.getUTCMonth();
  switch (preset) {
    case 'thisMonth':
      return { from: isoDate(new Date(Date.UTC(y, m, 1))), to: isoDate(today) };
    case 'lastMonth':
      return {
        from: isoDate(new Date(Date.UTC(y, m - 1, 1))),
        to: isoDate(new Date(Date.UTC(y, m, 0))), // last day of previous month
      };
    case 'last3Months':
      return { from: isoDate(new Date(Date.UTC(y, m - 2, 1))), to: isoDate(today) };
    case 'thisYear':
      return { from: isoDate(new Date(Date.UTC(y, 0, 1))), to: isoDate(today) };
    case 'custom':
    default:
      return { from: isoDate(today), to: isoDate(today) };
  }
}

type Props = {
  preset: Preset;
  from: string;
  to: string;
  onChange: (next: { preset: Preset; from: string; to: string }) => void;
};

export function DateRangePicker({ preset, from, to, onChange }: Props) {
  const presets = useMemo<Preset[]>(() => ['thisMonth', 'lastMonth', 'last3Months', 'thisYear', 'custom'], []);

  function selectPreset(p: Preset) {
    if (p === 'custom') {
      onChange({ preset: 'custom', from, to });
      return;
    }
    const r = rangeFor(p);
    onChange({ preset: p, from: r.from, to: r.to });
  }

  return (
    <Stack direction={{ base: 'column', md: 'row' }} gap="2" align={{ base: 'stretch', md: 'center' }}>
      <HStack gap="1" flexWrap="wrap">
        {presets.map((p) => (
          <Button
            key={p}
            size="xs"
            variant={preset === p ? 'solid' : 'outline'}
            colorPalette="brand"
            onClick={() => selectPreset(p)}
          >
            {PRESET_LABEL[p]}
          </Button>
        ))}
      </HStack>
      {preset === 'custom' && (
        <HStack gap="2">
          <Input
            type="date"
            size="sm"
            value={from}
            onChange={(e) => onChange({ preset: 'custom', from: e.currentTarget.value, to })}
            aria-label="Desde"
          />
          <Input
            type="date"
            size="sm"
            value={to}
            onChange={(e) => onChange({ preset: 'custom', from, to: e.currentTarget.value })}
            aria-label="Hasta"
          />
        </HStack>
      )}
    </Stack>
  );
}
